import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, RefreshCcw, Send, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  qualified: 'bg-emerald-100 text-emerald-800',
  won: 'bg-[#0B3B2E] text-[#F5F1E8]',
  lost: 'bg-rose-100 text-rose-800',
};

export default function PartnerDashboard() {
  const { partner, authHeader, API, refresh } = usePartnerAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes] = await Promise.all([
        axios.get(`${API}/partner/leads`, { headers: authHeader() }),
        refresh(),
      ]);
      setLeads(lRes.data.leads || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load leads'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader, refresh]);

  useEffect(() => { load(); }, [load]);

  const isApproved = partner.status === 'approved';

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">
            Partner dashboard
          </p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">
            Welcome, {partner.name.split(' ')[0]}
          </h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">{partner.company}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline"
            className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Link to="/partner/leads/new">
            <Button className="h-10 rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F]">
              <Send className="w-4 h-4 mr-1.5" /> Submit a referral
            </Button>
          </Link>
        </div>
      </div>

      {/* Status banner */}
      {!isApproved && (
        <div className={`mt-6 rounded-2xl p-5 lg:p-6 flex items-start gap-4 ${
          partner.status === 'rejected'
            ? 'bg-rose-50 border border-rose-200'
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-[#0B3B2E]" />
          </div>
          <div>
            <h3 className="font-serif text-[18px] text-[#0B3B2E]">
              {partner.status === 'rejected'
                ? 'Your application was declined'
                : partner.status === 'suspended'
                ? 'Your account is suspended'
                : 'Your application is under review'}
            </h3>
            <p className="mt-1 text-[13px] text-[#0E1F1A]/70">
              {partner.status === 'rejected'
                ? 'Please contact partners@vectorfx.co.uk for more information.'
                : partner.status === 'suspended'
                ? 'Please contact partners@vectorfx.co.uk to reinstate the account.'
                : 'A Vector FX admin will review your details, usually within one business day. Once approved you can start submitting referrals.'}
            </p>
          </div>
        </div>
      )}

      {isApproved && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 lg:p-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="font-serif text-[18px] text-[#0B3B2E]">You are approved</h3>
            <p className="mt-1 text-[13px] text-[#0E1F1A]/70">
              Submit a referral and Vector FX will handle the rest. You will see the status of each referral update in real time below.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total referrals" value={leads.length} />
        <Stat label="In progress" value={leads.filter((l) => ['new', 'contacted', 'qualified'].includes(l.status)).length} />
        <Stat label="Won" value={leads.filter((l) => l.status === 'won').length} />
        <Stat label="Lost" value={leads.filter((l) => l.status === 'lost').length} />
      </div>

      {/* Commission earned (from won leads) */}
      <CommissionCard leads={leads} rate={partner.commission_pct ?? 5} />

      {/* Referrals table */}
      <div className="mt-6 bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#0B3B2E]/10">
          <h2 className="font-serif text-[20px] text-[#0B3B2E]">Your referrals</h2>
        </div>
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#0B3B2E]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="p-16 text-center text-[#0E1F1A]/55 text-[14px]">
            No referrals yet. {isApproved && <Link to="/partner/leads/new" className="underline text-[#0B3B2E]">Submit your first one</Link>}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
                <tr>
                  <Th>Name</Th><Th>Email</Th><Th>Interest</Th><Th>Status</Th><Th>Submitted</Th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#0B3B2E]/8">
                    <Td className="font-medium text-[#0B3B2E]">{l.name}</Td>
                    <Td>{l.email}</Td>
                    <Td className="capitalize">{l.interest}</Td>
                    <Td>
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${STATUS_STYLES[l.status] || ''}`}>
                        {l.status}
                      </span>
                    </Td>
                    <Td className="text-[#0E1F1A]/55 whitespace-nowrap">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString('en-GB') : '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-5">
      <p className="text-[11px] uppercase tracking-wider text-[#0E1F1A]/55">{label}</p>
      <p className="mt-2 font-serif text-[32px] text-[#0B3B2E] leading-none">{value}</p>
    </div>
  );
}

function CommissionCard({ leads, rate }) {
  // Sum deal_value by currency across won leads, then apply commission %.
  const wonByCcy = {};
  for (const l of leads) {
    if (l.status !== 'won' || l.deal_value === null || l.deal_value === undefined) continue;
    const c = (l.deal_currency || 'GBP').toUpperCase();
    wonByCcy[c] = (wonByCcy[c] || 0) + Number(l.deal_value);
  }
  const entries = Object.entries(wonByCcy);

  return (
    <div className="mt-4 bg-[#0B3B2E] text-[#F5F1E8] rounded-2xl p-6 lg:p-7">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#C8944B]">Commission earned</p>
          <p className="mt-1 text-[13px] text-[#F5F1E8]/70">
            {rate}% of the value of your won referrals. Paid monthly, after settlement.
          </p>
        </div>
        <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#C8944B]/20 text-[#C8944B]">
          {rate}% rate
        </span>
      </div>
      <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
        {entries.length === 0 ? (
          <p className="font-serif text-[28px] text-[#F5F1E8]/60">No won deals yet</p>
        ) : (
          entries.map(([c, v]) => {
            const earned = v * (rate / 100);
            return (
              <div key={c}>
                <p className="text-[10px] uppercase tracking-wider text-[#F5F1E8]/55">{c}</p>
                <p className="font-serif text-[30px] lg:text-[36px] leading-none">
                  {earned.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </p>
                <p className="text-[11px] text-[#F5F1E8]/55 mt-1">
                  on {v.toLocaleString('en-GB', { maximumFractionDigits: 0 })} in won deals
                </p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">{children}</th>; }
function Td({ children, className = '' }) { return <td className={`px-4 py-3 text-[13px] text-[#0E1F1A]/85 ${className}`}>{children}</td>; }
